import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import client from '../API/API';
import KpiMachines from '../containers/KpiMachines';
import StatusMachines from '../containers/StatusMachines';
import AlarmsContainer from '../containers/AlarmsContainer';
import DetailsMachines from '../containers/DetailsMachines';
import ControlMachines from '../containers/ControlMachines';
import StatusPill from '../components/ui/StatusPill';
import { PageLoader } from '../components/ui/Skeleton';

import { Icon, ICONS } from '../components/ui/Icon';

export default function SubMachine() {
  const params   = useParams();
  const navigate = useNavigate();

  const [tabs,           setTabs]           = useState([]);
  const [activeTab,      setActiveTab]      = useState('details');
  const [fullScreen,     setFullscreen]     = useState(false);
  const [statusChannels, setStatusChannels] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const divRef = useRef(null);

  // Which tabs this user can see
  useEffect(() => {
    client.get('/api/machine_module/', {
      headers: { 'Content-Type': 'application/json', 'user-id': localStorage.getItem('userid') },
    }).then(res => {
      if (res.data.status === 'login_required') {
        localStorage.clear(); navigate('/login'); return;
      }
      setTabs(res.data.sub_pages || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [navigate]);

  // Fetch machine's status_channels config
  useEffect(() => {
    client.get('/api/machines/').then(r => {
      const m = (r.data.machines || []).find(m => m.machine_id === params.machine);
      if (m) setStatusChannels(m.status_channels || []);
    }).catch(console.error);
  }, [params.machine]);

  // Full-screen toggle
  const handleFullScreen = () => {
    if (!document.fullscreenElement) {
      divRef.current?.requestFullscreen().then(() => setFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setFullscreen(false));
    }
  };

  const allTabs = [
    { id: 9,  value: 'details',  label: 'Details' },
    { id: 6,  value: 'kpi',      label: 'KPI'     },
    { id: 7,  value: 'iostatus', label: 'Status'  },
    { id: 8,  value: 'alarms',   label: 'Alarms'  },
    { id: 10, value: 'control',  label: 'Control' },
  ];
  const visibleTabs = allTabs.filter(t => tabs.includes(t.id));

  if (loading) return <PageLoader />;

  return (
    <div ref={divRef} style={{ background: fullScreen ? 'var(--bg-app)' : 'transparent', padding: fullScreen ? '24px' : 0 }}>

      {/* ── Machine header band ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, flexWrap: 'wrap', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--r-md)', padding: '6px 12px',
              fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)',
              cursor: 'pointer', transition: 'all var(--t-fast)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <Icon d={ICONS.arrowLeft} size={14} />
            Back
          </button>

          {/* Machine ID + name */}
          <div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 15,
              color: 'var(--ink-900)',
            }}>
              {params.machine}
            </div>
            {params.nodeid && params.nodeid !== params.machine && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
                Node: {params.nodeid}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Fullscreen toggle */}
          <button
            onClick={handleFullScreen}
            style={{
              background: 'none', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--r-md)', padding: '6px 10px', cursor: 'pointer',
              color: 'var(--ink-500)', display: 'flex', alignItems: 'center',
              transition: 'background var(--t-fast)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <Icon d={ICONS.fullscreen} size={16} />
          </button>
        </div>
      </motion.div>

      {/* ── Tab bar ── */}
      {visibleTabs.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.05 }}
          style={{ marginBottom: 20 }}
        >
          <div className="atoms-tabs">
            {visibleTabs.map(tab => (
              <button
                key={tab.value}
                className={`atoms-tab ${activeTab === tab.value ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* ── Tab panels ── */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        {activeTab === 'kpi' && (
          <KpiMachines machineid={params.machine} screensize={fullScreen} />
        )}
        {activeTab === 'iostatus' && (
          <StatusMachines machineid={params.machine} statusChannels={statusChannels} />
        )}
        {activeTab === 'alarms' && (
          <AlarmsContainer machineid={params.machine} />
        )}
        {activeTab === 'details' && (
          <DetailsMachines nodeid={params.nodeid || params.machine} machineid={params.machine} />
        )}
        {activeTab === 'control' && (
          <ControlMachines nodeid={params.nodeid || params.machine} machineid={params.machine} />
        )}
      </motion.div>
    </div>
  );
}
